// src/app/research-themes/page.tsx - Updated with modular color system
'use client';

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Help as QuestionIcon,
  GpsFixed as TargetIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import Image from 'next/image';

// Import centralized types and hooks
import type { 
  ResearchTheme, 
} from '@/types';
import { useResearchThemes } from '@/hooks/useApi';
import { getMediaUrl } from '@/lib/api';
import React, { useEffect, useRef } from 'react';

// Import our modular color system
import { getActiveColorTheme, withOpacity } from '@/lib/colors';

// Get active colors
const colors = getActiveColorTheme();

// =====================
// STYLED COMPONENTS
// =====================

const HeroSection = styled(Box)(() => ({
  minHeight: '40vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  background: colors.background.hero,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("/api/placeholder/1920/600") center/cover',
    opacity: 0.2,
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colors.effects.overlay,
    zIndex: 2,
  }
}));

const HeroContent = styled(Box)({
  position: 'relative',
  zIndex: 3,
  color: colors.text.primary,
  textAlign: 'center',
});

const ThemeCard = styled(Card)(() => ({
  background: colors.gradients.card,
  border: `2px solid ${colors.border.primary}`,
  borderRadius: 16, // Using theme.spacing(2) equivalent
  marginBottom: 32, // Using theme.spacing(4) equivalent
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'visible', // Allow theme number to overflow
  
  '&:hover': {
    borderColor: colors.border.accent,
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 40px ${colors.effects.glow}`,
    
    '& .theme-number': {
      background: colors.gradients.primary,
      color: colors.brand.accent.contrastText,
      borderColor: colors.border.accent,
    },
  },
}));

const ThemeNumber = styled(Box)(() => ({
  position: 'absolute',
  top: '-20px',
  right: 24, // theme.spacing(3)
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: withOpacity(colors.brand.accent.main, 0.15),
  border: `3px solid ${withOpacity(colors.brand.accent.main, 0.6)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '1.5rem',
  color: colors.brand.accent.main,
  transition: 'all 0.3s ease',
  zIndex: 10,
}));

const ThemeImageContainer = styled(Box)(() => ({
  width: '100%',
  height: '200px',
  position: 'relative',
  borderRadius: '16px 16px 0 0', // theme.spacing(2)
  overflow: 'hidden',
  background: colors.background.elevated,
}));

const SectionBox = styled(Box)(() => ({
  marginBottom: 24, // theme.spacing(3)
  
  '& .section-icon': {
    color: colors.brand.accent.main,
    marginRight: 8, // theme.spacing(1)
  },
  
  '& .section-title': {
    color: colors.brand.accent.main,
    fontWeight: 600,
    marginBottom: 8, // theme.spacing(1)
    display: 'flex',
    alignItems: 'center',
  },
  
  '& .section-content': {
    color: colors.text.secondary,
    lineHeight: 1.6,
  },
}));

const PartnerChip = styled(Chip)(() => ({
  background: withOpacity(colors.brand.accent.main, 0.1),
  color: colors.text.secondary,
  border: `1px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
  margin: 4, // theme.spacing(0.5)
  
  '&:hover': {
    background: withOpacity(colors.brand.accent.main, 0.2),
    borderColor: colors.border.accent,
  },
}));

const ResearcherAvatar = styled(Avatar)(() => ({
  width: 40,
  height: 40,
  marginRight: 8, // theme.spacing(1)
  border: `2px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
  
  '&:hover': {
    borderColor: colors.border.accent,
  },
}));

const StatsContainer = styled(Box)(() => ({
  display: 'flex',
  gap: 16, // theme.spacing(2)
  marginTop: 16, // theme.spacing(2)
  flexWrap: 'wrap',
}));

const StatChip = styled(Chip)(() => ({
  background: withOpacity(colors.brand.accent.main, 0.1),
  color: colors.text.secondary,
  border: `1px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
  fontSize: '0.8rem',
  
  '& .MuiChip-icon': {
    color: colors.brand.accent.main,
    fontSize: '1rem',
  },
}));

// =====================
// SUB-COMPONENTS
// =====================

interface ThemeCardComponentProps {
  theme: ResearchTheme;
  isExpanded: boolean;
  onToggle: () => void;
}

const ThemeCardComponent = React.memo<ThemeCardComponentProps>(({ 
  theme, 
  isExpanded, 
  onToggle 
}) => {
  const getShortTitle = (title: string, themeNumber: string) => {
    return title.replace(new RegExp(`^${themeNumber}:?\\s*`, 'i'), '');
  };

  return (
    <ThemeCard>
      <ThemeNumber className="theme-number">
        {theme.themeNumber}
      </ThemeNumber>
      
      {/* Theme Image */}
      {theme.featuredImage && (
        <ThemeImageContainer>
          <Image
            src={getMediaUrl(theme.featuredImage)}
            alt={`${theme.title} illustration`}
            fill
            style={{
              objectFit: 'cover',
            }}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              height: '50%',
            }}
          />
        </ThemeImageContainer>
      )}
      
      <CardContent sx={{ p: 4, pt: theme.featuredImage ? 2 : 4 }}>
        {/* Theme Title */}
        <Typography
          variant="h4"
          sx={{
            color: colors.brand.accent.main,
            fontWeight: 700,
            marginBottom: 3,
            marginRight: 10, // Space for theme number
            fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
            lineHeight: 1.3,
          }}
        >
          {getShortTitle(theme.title, theme.themeNumber)}
        </Typography>
        
        {/* Theme Overview */}
        <SectionBox>
          <Typography variant="h6" className="section-title">
            <ScienceIcon className="section-icon" />
            Overview
          </Typography>
          <Typography variant="body1" className="section-content">
            {theme.overview}
          </Typography>
        </SectionBox>

        {/* Expandable Content */}
        <Accordion
          expanded={isExpanded}
          onChange={onToggle}
          sx={{
            display: 'none', // <-- Completely hides it
            background: withOpacity(colors.text.primary, 0.02),
            border: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}`,
            borderRadius: 2,
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: colors.brand.accent.main }} />}
            sx={{
              '& .MuiAccordionSummary-content': {
                color: colors.brand.accent.main,
                fontWeight: 600,
              },
            }}
          >
            <Typography>View Details & Research Information</Typography>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 3 }}>
            {/* Objectives */}
            {theme.objectives && (
              <SectionBox>
                <Typography variant="h6" className="section-title">
                  <TargetIcon className="section-icon" />
                  Objectives
                </Typography>
                <Typography variant="body1" className="section-content">
                  {theme.objectives}
                </Typography>
              </SectionBox>
            )}

            {/* Key Questions */}
            {theme.keyQuestions && (
              <SectionBox>
                <Typography variant="h6" className="section-title">
                  <QuestionIcon className="section-icon" />
                  Key Research Questions
                </Typography>
                <Typography variant="body1" className="section-content">
                  {theme.keyQuestions}
                </Typography>
              </SectionBox>
            )}

            {/* Lead Researchers */}
            {theme.leadResearchers && theme.leadResearchers.length > 0 && (
              <SectionBox>
                <Typography variant="h6" className="section-title">
                  <GroupIcon className="section-icon" />
                  Lead Researchers
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {theme.leadResearchers.map((researcher) => {
                    // Handle both data structures - sometimes researchers come with .attributes, sometimes directly
                    const researcherData = researcher.attributes || researcher;
                    
                    return (
                      <Box key={researcher.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ResearcherAvatar>
                          {researcherData.photo?.data ? (
                            <Image
                              src={getMediaUrl(researcherData.photo.data)}
                              alt={researcherData.name}
                              width={40}
                              height={40}
                              style={{ objectFit: 'cover' }}
                            />
                          ) : researcherData.photo ? (
                            <Image
                              src={getMediaUrl(researcherData.photo)}
                              alt={researcherData.name}
                              width={40}
                              height={40}
                              style={{ objectFit: 'cover' }}
                            />
                          ) : (
                            researcherData.name?.charAt(0) || '?'
                          )}
                        </ResearcherAvatar>
                        <Box>
                          <Typography variant="body2" sx={{ color: colors.text.secondary, fontWeight: 500 }}>
                            {researcherData.name || 'Unknown Researcher'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                            {researcherData.title || 'Researcher'}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </SectionBox>
            )}

            {/* Related Partners */}
            {theme.relatedPartners && theme.relatedPartners.length > 0 && (
              <SectionBox>
                <Typography variant="h6" className="section-title">
                  <BusinessIcon className="section-icon" />
                  Related Partners
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {theme.relatedPartners.map((partner) => {
                    // Handle both data structures - sometimes partners come with .attributes, sometimes directly
                    const partnerData = partner.attributes || partner;
                    
                    return (
                      <PartnerChip
                        key={partner.id}
                        label={partnerData.name || 'Unknown Partner'}
                        onClick={() => {
                          if (partnerData.website) {
                            window.open(partnerData.website, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        clickable={!!partnerData.website}
                      />
                    );
                  })}
                </Box>
              </SectionBox>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Statistics */}
        <StatsContainer>
          {theme.leadResearchers && theme.leadResearchers.length > 0 && (
            <StatChip
              icon={<GroupIcon />}
              label={`${theme.leadResearchers.length} Lead Researcher${theme.leadResearchers.length !== 1 ? 's' : ''}`}
              size="small"
            />
          )}
          {theme.researchers && theme.researchers.length > 0 && (
            <StatChip
              icon={<GroupIcon />}
              label={`${theme.researchers.length} Total Researcher${theme.researchers.length !== 1 ? 's' : ''}`}
              size="small"
            />
          )}
          {theme.relatedPartners && theme.relatedPartners.length > 0 && (
            <StatChip
              icon={<BusinessIcon />}
              label={`${theme.relatedPartners.length} Partner${theme.relatedPartners.length !== 1 ? 's' : ''}`}
              size="small"
            />
          )}
          {theme.relatedActivities && theme.relatedActivities.length > 0 && (
            <StatChip
              icon={<ScienceIcon />}
              label={`${theme.relatedActivities.length} Activit${theme.relatedActivities.length !== 1 ? 'ies' : 'y'}`}
              size="small"
            />
          )}
        </StatsContainer>
      </CardContent>
    </ThemeCard>
  );
});

ThemeCardComponent.displayName = 'ThemeCardComponent';

const LoadingSkeleton = React.memo(() => (
  <Container maxWidth="lg" sx={{ py: 6 }}>
    {Array.from({ length: 6 }, (_, index) => (
      <Card 
        key={index} 
        sx={{ 
          background: colors.gradients.card, 
          mb: 4, 
          border: `1px solid ${colors.border.primary}`,
          position: 'relative',
        }}
      >
        <Skeleton 
          variant="circular" 
          width={80} 
          height={80} 
          sx={{ 
            bgcolor: withOpacity(colors.text.primary, 0.1),
            position: 'absolute',
            top: -20,
            right: 24,
            zIndex: 10,
          }} 
        />
        <Skeleton 
          variant="rectangular" 
          height={200} 
          sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
        />
        <CardContent sx={{ p: 4 }}>
          <Skeleton 
            variant="text" 
            height={40} 
            width="70%" 
            sx={{ bgcolor: withOpacity(colors.text.primary, 0.15), mb: 3 }} 
          />
          <Skeleton 
            variant="text" 
            height={20} 
            sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 1 }} 
          />
          <Skeleton 
            variant="text" 
            height={20} 
            sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 1 }} 
          />
          <Skeleton 
            variant="text" 
            height={20} 
            width="60%" 
            sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
          />
        </CardContent>
      </Card>
    ))}
  </Container>
));

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const ResearchThemesPage: React.FC = () => {
  const { themes, loading, error } = useResearchThemes();
  const [expandedTheme, setExpandedTheme] = React.useState<string | null>(null);
  const hasScrolledToTheme = useRef(false);

    useEffect(() => {
    if (themes.length > 0 && !hasScrolledToTheme.current) {
      const hash = window.location.hash;
      if (hash.startsWith('#theme-')) {
        const themeId = hash.replace('#theme-', '');
        const theme = themes.find(t => t.id.toString() === themeId);
        
        if (theme) {
          // Expand the theme
          setExpandedTheme(themeId);
          
          // Scroll to the theme after a short delay to ensure rendering
          setTimeout(() => {
            const element = document.getElementById(`theme-${themeId}`);
            if (element) {
              element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
              });
              hasScrolledToTheme.current = true;
            }
          }, 500);
        }
      }
    }
  }, [themes]);

  const handleThemeToggle = React.useCallback((themeId: string) => {
    setExpandedTheme(prev => prev === themeId ? null : themeId);
    
    // Update URL hash when expanding
    if (expandedTheme !== themeId) {
      window.history.pushState(null, '', `#theme-${themeId}`);
    } else {
      // Remove hash when collapsing
      window.history.pushState(null, '', window.location.pathname);
    }
  }, [expandedTheme]);


  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
        <HeroSection>
          <Container maxWidth="lg">
            <HeroContent>
              <Skeleton 
                variant="text" 
                height={60} 
                width="60%" 
                sx={{ bgcolor: withOpacity(colors.text.primary, 0.15), mx: 'auto', mb: 2 }} 
              />
              <Skeleton 
                variant="text" 
                height={24} 
                width="80%" 
                sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mx: 'auto' }} 
              />
            </HeroContent>
          </Container>
        </HeroSection>
        <LoadingSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              background: colors.gradients.card,
              border: `1px solid ${colors.border.primary}`,
            }}
          >
            <Typography variant="h5" sx={{ color: colors.text.secondary, mb: 2 }}>
              Unable to Load Research Themes
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text.disabled, mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="outlined"
              sx={{
                color: colors.brand.accent.main,
                borderColor: colors.brand.accent.main,
                '&:hover': {
                  background: withOpacity(colors.brand.accent.main, 0.1),
                }
              }}
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <HeroContent>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                marginBottom: 2,
                background: colors.gradients.secondary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Research Themes
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                fontWeight: 400,
                color: colors.text.secondary,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.5,
              }}
            >
              Exploring Six Distinct Areas of Cybersecurity Research and Innovation
            </Typography>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          sx={{
            p: 4,
            mb: 6,
            background: colors.background.elevated,
            border: `1px solid ${colors.border.secondary}`,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: colors.brand.accent.main,
              fontWeight: 600,
              marginBottom: 3,
              textAlign: 'center',
            }}
          >
            Program Overview
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.text.secondary,
              fontSize: '1.1rem',
              lineHeight: 1.7,
              textAlign: 'center',
              maxWidth: '900px',
              mx: 'auto',
            }}
          >
            Our comprehensive research program encompasses six distinct themes, each addressing critical 
            challenges in cybersecurity for emerging technologies. From post-quantum cryptography to 
            human factors in security, these themes represent a holistic approach to understanding and 
            solving the cybersecurity challenges of tomorrow. Each theme brings together leading researchers, 
            industry partners, and cutting-edge technologies to advance the state of the art in their 
            respective domains.
          </Typography>
        </Paper>

        {/* Research Themes */}
        {themes.length > 0 ? (
          themes.map((theme) => (
            <div key={theme.id} id={`theme-${theme.id}`}> {/* Add id for scrolling */}
                <ThemeCardComponent
                key={theme.id}
                theme={theme}
                isExpanded={expandedTheme === theme.id.toString()}
                onToggle={() => handleThemeToggle(theme.id.toString())}
                />
            </div>
          ))
        ) : (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              background: colors.gradients.card,
              border: `1px solid ${colors.border.primary}`,
            }}
          >
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
              No Research Themes Available
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Research themes will be displayed here once they are added to the system.
            </Typography>
          </Paper>
        )}

        {/* Call to Action */}
        {themes.length > 0 && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography
              variant="h5"
              sx={{
                color: colors.brand.accent.main,
                fontWeight: 600,
                marginBottom: 2,
              }}
            >
              Interested in Our Research?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                marginBottom: 3,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Learn more about our program opportunities, partnerships, and how you can get involved 
              in cutting-edge cybersecurity research.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: colors.gradients.primary,
                color: colors.brand.accent.contrastText,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  background: colors.gradients.primary,
                  filter: 'brightness(1.1)',
                }
              }}
            >
              Contact Us
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ResearchThemesPage;